import React, { useState } from 'react';
import './RiskScale.css';
import { Typography, Box, Modal } from '@mui/material';
import { InfoOutlineRounded } from '@mui/icons-material';
import { theme } from './theme';

/**
 * RiskScale component: visually represents the user's apnea risk on a color-coded scale.
 * @param {number} level - The risk score (1-8)
 * @param {string} apneaLikelihood - 'low', 'moderate', or 'high'
 */
export default function RiskScale({ level = 8, apneaLikelihood = 'high', ptb = 20 }) {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  
  let apneaRiskWording = "bajo";
  if (apneaLikelihood === "moderate") {
    apneaRiskWording = "moderado";
  } else if (apneaLikelihood === "high") {
    apneaRiskWording = "alto";
  }
  const handleApneaClick = () => {
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
  };

  return (
    <div className="risk-scale-section" >
      <Box paddingBottom={0} />
      
      <Typography variant="h1" sx={{ alignSelf: 'flex-start', fontWeight: 500, mb: 1, letterSpacing: 0, textAlign: 'left', width: '100%' }}>
        <strong style={{color: level >= 5 ? '#E53E3E' : level >= 3 ? '#DD6B20' : '#38A169'}}>Riesgo {apneaRiskWording}</strong> de{' '}
        <span 
          onClick={handleApneaClick}
          style={{ 
            textDecoration: 'underline', 
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          apnea del sueño
          <InfoOutlineRounded sx={{ width: '25px', height: '25px', fontSize: 'inherit', color: 'inherit' }} />
        </span>
      </Typography>

      <div className="new-risk-container">
        {/* Main Scale Bar */}
        <div className="gradient-scale-bar">
          {/* Numbers 1-8 */}
          <div className="scale-numbers-row">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((number) => (
              <span 
                key={number} 
                className={`scale-number ${number === level ? 'active' : ''}`}
              >
                {number}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Sleep Apnea Information Modal */}
      <Modal
        open={isPopupOpen}
        onClose={handleClosePopup}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 2
        }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            maxWidth: 600,
            maxHeight: '80vh',
            overflow: 'auto',
            outline: 'none'
          }}
        >
          <Typography variant="h2" sx={{ mb: 2, fontWeight: 600 }}>
            ¿Qué es la apnea del sueño?
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            La apnea del sueño es un trastorno común pero serio donde la respiración se interrumpe repetidamente durante el sueño. Estas pausas pueden durar desde unos segundos hasta más de un minuto y pueden ocurrir hasta 30 veces o más por hora.
          </Typography>
          <Typography variant="h3" sx={{ mb: 1, fontWeight: 600 }}>
            Tipos principales:
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            • <strong>Apnea obstructiva del sueño (AOS):</strong> La más común, causada por el bloqueo de las vías respiratorias<br/>
            • <strong>Apnea central del sueño:</strong> El cerebro no envía las señales correctas a los músculos respiratorios<br/>
            • <strong>Apnea mixta:</strong> Combinación de ambas
          </Typography>
          <Typography variant="h3" sx={{ mb: 1, fontWeight: 600 }}>
            ¿Por qué es importante tratarla?
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Sin tratamiento, la apnea del sueño puede aumentar el riesgo de hipertensión, enfermedades cardíacas, accidentes cerebrovasculares, diabetes tipo 2 y otros problemas de salud graves.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button 
              onClick={handleClosePopup}
              style={{
                padding: '10px 20px',
                backgroundColor: theme.palette.bunoColors.spaceBlue,
                color: 'white',
                border: 'none',
                borderRadius: '20px',
                cursor: 'pointer'
              }}
            >
              Cerrar
            </button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
}
